import React, { useMemo } from "react";

/* ============================================================================
   SPECIFICATIONPROJECTREPORT.JSX
   ----------------------------------------------------------------------------
   DOSSIER DES SPÉCIFICATIONS PROJET

   FINALITÉ
   - produire un document transmissible au client
   - servir de livrable d’ingénierie
   - présenter une synthèse claire puis un cahier des spécifications structuré
   - rester imprimable, lisible et exploitable contractuellement

   POSITIONNEMENT
   - document de sortie
   - pas un export d’interface
   - pas un tableau de bord
   - pas un support interne logiciel

   STRUCTURE
   1. Page de garde
   2. Synthèse de dossier
   3. Objet et périmètre du document
   4. Synthèse des spécifications
   5. Cahier des spécifications
   6. Points ouverts et réserves
   7. Annexes
============================================================================ */

const SpecificationProjectReport = ({
  projectIdentity = {},
  specificationArticles = [],
  reviewItems = [],
  reportConfig = {},
}) => {
  const config = {
    showSummary: reportConfig.showSummary !== false,
    showScope: reportConfig.showScope !== false,
    showOverview: reportConfig.showOverview !== false,
    showArticles: reportConfig.showArticles !== false,
    showOpenPoints: reportConfig.showOpenPoints !== false,
    showAppendices: reportConfig.showAppendices !== false,

    includeArticleStatus: reportConfig.includeArticleStatus !== false,
    includeProjectScope: reportConfig.includeProjectScope !== false,
    includeOpenPointsInBody: reportConfig.includeOpenPointsInBody !== false,

    issueLabel: reportConfig.issueLabel || "Version provisoire",
  };

  const safeArticles = Array.isArray(specificationArticles)
    ? specificationArticles
    : [];

  const safeReview = Array.isArray(reviewItems) ? reviewItems : [];

  const groupedArticles = useMemo(() => {
    const groups = groupBy(safeArticles, (item) => item.family || "Général");

    return Object.keys(groups)
      .sort(alphaSort)
      .map((family) => ({
        family,
        items: [...groups[family]].sort(articleSort),
      }));
  }, [safeArticles]);

  const summary = useMemo(() => {
    const totalArticles = safeArticles.length;

    const attributedArticles = safeArticles.filter(
      (item) => normalizeUsageStatus(item.usageStatus) === "ATTRIBUE"
    ).length;

    const partialArticles = safeArticles.filter(
      (item) => normalizeUsageStatus(item.usageStatus) === "PARTIEL"
    ).length;

    const nonAttributedArticles = safeArticles.filter(
      (item) => normalizeUsageStatus(item.usageStatus) === "NON_ATTRIBUE"
    ).length;

    const familiesCount = new Set(
      safeArticles.map((item) => item.family || "Général")
    ).size;

    const disciplineCount = new Set(
      safeArticles.flatMap((item) =>
        Array.isArray(item.disciplines) ? item.disciplines : []
      )
    ).size;

    return {
      totalArticles,
      attributedArticles,
      partialArticles,
      nonAttributedArticles,
      familiesCount,
      disciplineCount,
      reviewCount: safeReview.length,
    };
  }, [safeArticles, safeReview]);

  const overviewRows = useMemo(() => {
    return groupedArticles.map((group) => {
      const items = group.items;

      return {
        family: group.family,
        totalArticles: items.length,
        attributedArticles: items.filter(
          (item) => normalizeUsageStatus(item.usageStatus) === "ATTRIBUE"
        ).length,
        partialArticles: items.filter(
          (item) => normalizeUsageStatus(item.usageStatus) === "PARTIEL"
        ).length,
        nonAttributedArticles: items.filter(
          (item) => normalizeUsageStatus(item.usageStatus) === "NON_ATTRIBUE"
        ).length,
      };
    });
  }, [groupedArticles]);

  const appendices = useMemo(() => {
    return safeArticles
      .map((item) => ({
        code: item.code || "—",
        title: item.title || "—",
        family: item.family || "Général",
        chapter: item.chapter || "Général",
      }))
      .sort((a, b) =>
        alphaSort(`${a.code} ${a.title}`, `${b.code} ${b.title}`)
      );
  }, [safeArticles]);

  return (
    <div className="report-page">
      {/* ============================================================
         1. PAGE DE GARDE
      ============================================================ */}
      <section className="report-block">
        <div className="report-header">
          <div>
            <h2>Cahier des spécifications projet</h2>
            <p className="muted">
              Document de synthèse et de formalisation des exigences applicables
              au projet.
            </p>
          </div>

          <div className="report-date-block">
            <div className="report-meta-label">Date d’édition</div>
            <div className="report-meta-value">{formatToday()}</div>
          </div>
        </div>

        <div className="page-separator" />

        <div className="report-meta-grid">
          <ReportMetaItem label="Projet" value={projectIdentity.projectName} />
          <ReportMetaItem label="Référence" value={projectIdentity.reference} />
          <ReportMetaItem label="Client" value={projectIdentity.clientName} />
          <ReportMetaItem label="Localisation" value={projectIdentity.location} />
          <ReportMetaItem label="Type de projet" value={projectIdentity.projectType} />
          <ReportMetaItem label="Pays" value={projectIdentity.country} />
          <ReportMetaItem label="Indice" value={config.issueLabel} />
        </div>
      </section>

      {/* ============================================================
         2. SYNTHÈSE DE DOSSIER
      ============================================================ */}
      {config.showSummary && (
        <section className="report-block">
          <h3>Synthèse de dossier</h3>

          <div className="report-kpi-grid">
            <ReportKpiCard label="Familles traitées" value={summary.familiesCount} />
            <ReportKpiCard label="Articles rédigés" value={summary.totalArticles} />
            <ReportKpiCard label="Articles attribués" value={summary.attributedArticles} />
            <ReportKpiCard label="Articles partiels" value={summary.partialArticles} />
            <ReportKpiCard label="Disciplines concernées" value={summary.disciplineCount} />
            <ReportKpiCard label="Points ouverts" value={summary.reviewCount} />
          </div>

          <div className="report-paragraph-block">
            <p>
              Le présent document constitue la synthèse des spécifications
              applicables au projet, consolidées à partir des besoins
              fonctionnels, techniques, d’interface et d’exploitation identifiés
              à ce stade.
            </p>

            <p>
              Il a pour objet de formaliser les exigences à prendre en compte
              pour la conception, la coordination interdisciplinaire, la
              consultation des entreprises et la sécurisation des arbitrages
              techniques.
            </p>

            <p>
              À la présente édition, le dossier comprend{" "}
              <strong>{summary.totalArticles}</strong> articles répartis sur{" "}
              <strong>{summary.familiesCount}</strong> familles techniques. Parmi
              eux, <strong>{summary.attributedArticles}</strong> sont déjà
              pleinement rattachés au périmètre projet, tandis que{" "}
              <strong>{summary.partialArticles}</strong> restent à compléter ou à
              confirmer.
            </p>
          </div>
        </section>
      )}

      {/* ============================================================
         3. OBJET ET PÉRIMÈTRE
      ============================================================ */}
      {config.showScope && (
        <section className="report-block">
          <h3>Objet et périmètre du document</h3>

          <div className="report-paragraph-block">
            <p>
              Le présent cahier des spécifications a vocation à fixer les
              exigences de référence applicables aux ouvrages, équipements,
              systèmes et interfaces concernés par le projet.
            </p>

            <p>
              Il couvre notamment :
            </p>

            <ul className="report-list">
              <li>les exigences de performance et de service,</li>
              <li>les exigences techniques de conception et de dimensionnement,</li>
              <li>les exigences d’interface entre lots et disciplines,</li>
              <li>les exigences d’exploitation, de maintenance et de durabilité,</li>
              <li>les points nécessitant une confirmation ou un arbitrage.</li>
            </ul>

            <p>
              Le document est destiné à servir de base commune de travail pour
              le maître d’ouvrage, la maîtrise d’œuvre, les ingénieries
              spécialisées et, le cas échéant, les entreprises consultées.
            </p>
          </div>
        </section>
      )}

      {/* ============================================================
         4. SYNTHÈSE DES SPÉCIFICATIONS
      ============================================================ */}
      {config.showOverview && (
        <section className="report-block">
          <h3>Synthèse des spécifications</h3>

          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Famille technique</th>
                  <th>Nombre d’articles</th>
                  <th>Attribués</th>
                  <th>Partiels</th>
                  <th>Non attribués</th>
                </tr>
              </thead>
              <tbody>
                {overviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="report-empty-cell">
                      Aucune donnée de synthèse disponible.
                    </td>
                  </tr>
                ) : (
                  overviewRows.map((row) => (
                    <tr key={row.family}>
                      <td>{row.family}</td>
                      <td>{row.totalArticles}</td>
                      <td>{row.attributedArticles}</td>
                      <td>{row.partialArticles}</td>
                      <td>{row.nonAttributedArticles}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ============================================================
         5. CAHIER DES SPÉCIFICATIONS
      ============================================================ */}
      {config.showArticles && (
        <section className="report-block">
          <h3>Cahier des spécifications</h3>

          {groupedArticles.length === 0 ? (
            <div className="report-empty-cell">
              Aucun article de spécification disponible.
            </div>
          ) : (
            groupedArticles.map((group) => (
              <article key={group.family} className="report-article-family-block">
                <h4>{group.family}</h4>

                {group.items.map((item, index) => (
                  <div
                    key={item.id || `${item.code}-${index}`}
                    className="report-article-card"
                  >
                    <div className="report-article-header">
                      <div>
                        <div className="report-meta-label">
                          {item.code || "—"} · {item.chapter || "Général"}
                        </div>
                        <div className="report-article-title">
                          {item.title || "—"}
                        </div>
                      </div>

                      {config.includeArticleStatus && (
                        <div className="report-article-meta-inline">
                          <span className="report-inline-chip">
                            {item.kind || "—"}
                          </span>
                          <span className="report-inline-chip">
                            {item.status || "—"}
                          </span>
                          <span className="report-inline-chip">
                            {item.usageStatus || "—"}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="page-separator" />

                    <div className="report-article-section">
                      <div className="report-subsection-title">Exigence</div>
                      <div className="report-article-text">
                        {item.genericText || "—"}
                      </div>
                    </div>

                    {hasMeaningfulValue(item.projectVariant) && (
                      <div className="report-article-section">
                        <div className="report-subsection-title">
                          Précision ou adaptation projet
                        </div>
                        <div className="report-article-text">
                          {item.projectVariant}
                        </div>
                      </div>
                    )}

                    {hasMeaningfulValue(item.projectNote) && (
                      <div className="report-article-section">
                        <div className="report-subsection-title">
                          Observation technique
                        </div>
                        <div className="report-article-text">
                          {item.projectNote}
                        </div>
                      </div>
                    )}

                    {config.includeProjectScope && (
                      <div className="report-article-section">
                        <div className="report-subsection-title">
                          Champ d’application
                        </div>

                        <div className="report-meta-grid">
                          <ReportMetaItem
                            label="Ouvrages / éléments concernés"
                            value={formatArray(item.usedBy)}
                          />
                          <ReportMetaItem
                            label="Disciplines concernées"
                            value={formatArray(item.disciplines)}
                          />
                          <ReportMetaItem
                            label="Systèmes concernés"
                            value={formatArray(item.systems)}
                          />
                          <ReportMetaItem
                            label="Interfaces concernées"
                            value={formatArray(item.interfaces)}
                          />
                        </div>
                      </div>
                    )}

                    {config.includeOpenPointsInBody &&
                      hasMeaningfulValue(item.openPoint) && (
                        <div className="report-article-section">
                          <div className="report-subsection-title">
                            Point à confirmer
                          </div>
                          <div className="report-article-text">
                            {item.openPoint}
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </article>
            ))
          )}
        </section>
      )}

      {/* ============================================================
         6. POINTS OUVERTS ET RÉSERVES
      ============================================================ */}
      {config.showOpenPoints && (
        <section className="report-block">
          <h3>Points ouverts et réserves</h3>

          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Sujet</th>
                  <th>Périmètre</th>
                  <th>Nature</th>
                  <th>Impact</th>
                  <th>Décision / action attendue</th>
                </tr>
              </thead>
              <tbody>
                {safeReview.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="report-empty-cell">
                      Aucun point ouvert identifié.
                    </td>
                  </tr>
                ) : (
                  safeReview.map((item, index) => (
                    <tr key={item.id || `${item.title}-${index}`}>
                      <td>{item.title || "—"}</td>
                      <td>{item.perimeter || "—"}</td>
                      <td>{item.issueType || "—"}</td>
                      <td>{item.impact || "—"}</td>
                      <td>{item.decision || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ============================================================
         7. ANNEXES
      ============================================================ */}
      {config.showAppendices && (
        <section className="report-block">
          <h3>Annexes</h3>

          <div className="report-table-wrapper">
            <table className="report-table report-table-compact">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Article</th>
                  <th>Famille</th>
                  <th>Chapitre</th>
                </tr>
              </thead>
              <tbody>
                {appendices.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="report-empty-cell">
                      Aucune annexe disponible.
                    </td>
                  </tr>
                ) : (
                  appendices.map((row, index) => (
                    <tr key={`${row.code}-${index}`}>
                      <td>{row.code}</td>
                      <td>{row.title}</td>
                      <td>{row.family}</td>
                      <td>{row.chapter}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

/* ============================================================================
   SOUS-COMPOSANTS
============================================================================ */

const ReportMetaItem = ({ label, value }) => {
  return (
    <div className="report-meta-item">
      <div className="report-meta-label">{label}</div>
      <div className="report-meta-value">{value || "—"}</div>
    </div>
  );
};

const ReportKpiCard = ({ label, value }) => {
  return (
    <div className="report-kpi-card">
      <div className="report-kpi-label">{label}</div>
      <div className="report-kpi-value">{value}</div>
    </div>
  );
};

/* ============================================================================
   HELPERS
============================================================================ */

function normalizeUsageStatus(value) {
  const v = String(value || "").trim().toLowerCase();

  if (["attribué", "attribue", "attributed", "used"].includes(v)) {
    return "ATTRIBUE";
  }
  if (["partiel", "partial"].includes(v)) {
    return "PARTIEL";
  }
  if (["non attribué", "non attribue", "not attributed", "unused"].includes(v)) {
    return "NON_ATTRIBUE";
  }

  return "INCONNU";
}

function groupBy(items = [], selector) {
  return items.reduce((acc, item) => {
    const key = selector(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

function alphaSort(a, b) {
  return String(a || "").localeCompare(String(b || ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function articleSort(a, b) {
  return `${a.code || ""} ${a.title || ""}`.localeCompare(
    `${b.code || ""} ${b.title || ""}`,
    undefined,
    { sensitivity: "base", numeric: true }
  );
}

function hasMeaningfulValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function formatArray(value) {
  if (!Array.isArray(value) || value.length === 0) return "—";
  return value.join(", ");
}

function formatToday() {
  const date = new Date();
  return date.toLocaleDateString();
}

export default SpecificationProjectReport;